import { useEffect, useState } from 'react';
import { useClient } from 'urql';

import { RunStatus } from '@/__generated__/graphql';
import { GetModelExecutionHistoryStatus } from '@/hooks/discovery/modelExecutionHistoryStatus';
import { addDays, formatDateUtcYYYYMMDD } from '@/util/date';

interface ReusedCounts {
  reusedByModel: Record<string, number>;
  reusedByModelAndJob: Record<string, Record<number, number>>;
  fetching: boolean;
}

export const useMostExecutedModelsReusedCounts = (
  uniqueIds: string[],
  environmentId: number | undefined,
  startDate: string,
  endDate: string,
): ReusedCounts => {
  const client = useClient();
  const [reusedByModel, setReusedByModel] = useState<Record<string, number>>({});
  const [reusedByModelAndJob, setReusedByModelAndJob] = useState<
    Record<string, Record<number, number>>
  >({});
  const [fetching, setFetching] = useState(false);

  const uniqueIdsKey = uniqueIds.join(',');
  const inputsKey = `${uniqueIdsKey}_${startDate}_${endDate}`;
  const [prevInputsKey, setPrevInputsKey] = useState(inputsKey);

  // Synchronously mark as fetching when inputs change to avoid a flash of
  // unadjusted data between rawData arriving and the useEffect firing.
  if (inputsKey && inputsKey !== prevInputsKey) {
    setPrevInputsKey(inputsKey);
    setFetching(true);
    setReusedByModel({});
    setReusedByModelAndJob({});
  }

  useEffect(() => {
    if (!uniqueIdsKey || !environmentId || !startDate || !endDate) {
      setFetching(false);
      return;
    }

    const ids = uniqueIdsKey.split(',');
    let cancelled = false;

    const fetchReusedCounts = async () => {
      setFetching(true);

      try {
        const results = await Promise.all(
          ids.map((uniqueId) =>
            client
              .query(GetModelExecutionHistoryStatus, {
                environmentId,
                start: startDate,
                // API is end date exclusive
                end: formatDateUtcYYYYMMDD(addDays(new Date(endDate), 1)),
                uniqueId,
              })
              .toPromise()
              .then((result) => ({ uniqueId, result })),
          ),
        );

        if (cancelled) return;

        const modelCounts: Record<string, number> = {};
        const modelJobCounts: Record<string, Record<number, number>> = {};

        for (const { uniqueId, result } of results) {
          let totalReused = 0;
          const jobReused: Record<number, number> = {};

          const history = result.data?.performance?.modelExecutionHistory ?? [];
          for (const day of history) {
            for (const byJob of day.executionsByJob) {
              let jobReusedCount = 0;
              for (const execution of byJob.executions) {
                if (execution.status === RunStatus.Reused) {
                  jobReusedCount++;
                  totalReused++;
                }
              }
              if (jobReusedCount > 0) {
                jobReused[byJob.jobId] = (jobReused[byJob.jobId] ?? 0) + jobReusedCount;
              }
            }
          }

          modelCounts[uniqueId] = totalReused;
          modelJobCounts[uniqueId] = jobReused;
        }

        setReusedByModel(modelCounts);
        setReusedByModelAndJob(modelJobCounts);
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    };

    fetchReusedCounts();

    return () => {
      cancelled = true;
    };
  }, [client, uniqueIdsKey, environmentId, startDate, endDate]);

  return { reusedByModel, reusedByModelAndJob, fetching };
};
